import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { IContainer } from '../../types/container';
import { IFuzzySearchResult } from '../../types/system';

import { AppConfig } from '../../config/appConfig';

import { ErrorProvider } from '../../classes/errorProvider';
import { ContainerService } from '../../services/containerService';
import NumberField from '../shared/numberField';

import '../../styles/containerDetailsForm.scss';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

export interface IContainerDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: IContainer;
	reloadData: () => void;
}

const ExternalNameToolTip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.common.black,

	},
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 350,
		fontSize: theme.typography.pxToRem(14),
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white,
	},
}));

const ContainerDetailsForm: React.FunctionComponent<IContainerDetailsForm> = ({ action, id, details, reloadData }) => {
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<IContainer>(details);
	const [originalContainerName, setOriginalContainerName] = useState<string>(details?.name);
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	const doSave = async () => {
		clearSaveResult();
		setIsSaving(true);
		setWorkingModalParams({ params: { content: "Saving container details..." } });
		const payload: IContainer = { ...formFields };

		if (action == "add") {
			ContainerService.addContainer(payload)
				.then((response) => {
					console.log('ContainerService.addContainer: SUCCESS', response);
					setSuccessMessage("Container has been added.");
					setTimeout(() => {
						navigate("/container/" + response.data);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('ContainerService.addContainer: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					setWorkingModalParams(undefined);
					window.scroll(0, 0);
				});
		}
		else if (action == "edit" && id) {
			ContainerService.updateContainer(payload)
				.then((response) => {
					console.log('ContainerService.updateContainer: SUCCESS', response);
					reloadData();
					setSuccessMessage("Container details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('ContainerService.updateContainer: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
					setIsSaving(false);
				})
				.finally(() => {
					setWorkingModalParams(undefined);
					window.scrollTo(0, 0);
				});
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// Fuzzy check existing customer names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// Skip fuzzy duplicate check for now
		await doSave();

		// // If the name wasn't changed then don't bother checking for duplicates
		// if (action == "edit" && formFields?.name == originalContainerName) {
		// 	await doSave();
		// 	return;
		// }

		// setIsCheckingNameForDupes(true);

		// CustomerService.fuzzySearchForDuplicateCompanyNames({ name: formFields?.name, excludeId: action == "edit" ? +id : undefined })
		// 	.then(async (response) => {
		// 		console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: SUCCESS', response);
		// 		const matches: IFuzzySearchResult[] = response.data;
		// 		if (matches.length > 0) {
		// 			if (await UtilityService.ConfirmationModal(
		// 				confirm,
		// 				<div className="dupes-dialog-title">
		// 					<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
		// 					Potential Duplicates
		// 				</div>,
		// 				<div className="dupes-dialog-content">
		// 					The name <strong>{formFields.name}</strong> is similar to <strong>{matches.length}</strong> existing container{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
		// 					<ul>
		// 						{matches.map((item, index) => (
		// 							<li key={index}>
		// 								<a href={`/customer/${item.id}`} target="_blank" rel="noopener noreferrer">
		// 									<Icons.ViewItem height="14px"></Icons.ViewItem>
		// 									{item.name}
		// 								</a>
		// 							</li>
		// 						))}
		// 					</ul>
		// 				</div>,
		// 				{
		// 					confirmationText: "Continue",
		// 					confirmationButtonProps: { color: 'primary', variant: 'contained' },
		// 					cancellationText: "Cancel"
		// 				}
		// 			) == false) {
		// 				return;
		// 			}
		// 		}

		// 		await doSave();
		// 	})
		// 	.catch((error) => {
		// 		console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: ERROR', error);
		// 		setErrorMessage(ErrorProvider.getError(error));
		// 	})
		// 	.finally(() => {
		// 		setIsCheckingNameForDupes(false);
		// 	});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<section className="hwi-container-details-form">
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Container Name *</InputLabel>
						<TextField className="name-field" required autoComplete="off" name="name" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }} inputProps={{ maxLength: 50 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Weight</InputLabel>
						<NumberField className="weight-field" name="weight" value={formFields.weight} size="small" onChange={(event) => { setFormFields({ ...formFields, ["weight"]: +event.target.value }) }} />
					</div>
					<div className="row">
						<InputLabel>Length (inches)</InputLabel>
						<NumberField className="length-field" name="length" min={0} value={formFields.length} size="small" onChange={(event) => { setFormFields({ ...formFields, ["length"]: +event.target.value }) }} />
					</div>
					<div className="row">
						<InputLabel>Width (inches)</InputLabel>
						<NumberField className="width-field" name="width" min={0} value={formFields.width} size="small" onChange={(event) => { setFormFields({ ...formFields, ["width"]: +event.target.value }) }} />
					</div>
					<div className="row">
						<InputLabel>Height (inches)</InputLabel>
						<NumberField className="height-field" name="height" min={0} value={formFields.height} size="small" onChange={(event) => { setFormFields({ ...formFields, ["height"]: +event.target.value }) }} />
					</div>
					<div className="row">
						<InputLabel>Fills Position</InputLabel>
						<Select
							value={formFields.fillsPosition ? 1 : 0}
							className="fills-position-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["fillsPosition"]: event.target.value === 1 ? true : false }) }}
						>
							<MenuItem value={1} key={1}>Yes</MenuItem>
							<MenuItem value={0} key={0}>No</MenuItem>
						</Select>
					</div>
					<div className="row">
						<InputLabel>Status</InputLabel>
						<Select
							value={formFields.isActive ? 1 : 0}
							className="status-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["isActive"]: event.target.value === 1 ? true : false }) }}
						>
							<MenuItem value={1} key={1}>Active</MenuItem>
							<MenuItem value={0} key={0}>Inactive</MenuItem>
						</Select>
					</div>
					<div className="row">
						<InputLabel>External Name</InputLabel>
						<ExternalNameToolTip title="Substitute the Container Name with this in applicable documents to meet external vendor requirements." placement='right' arrow>
							<TextField className="external-name-field" placeholder="Optional" name="externalName" value={formFields.externalName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["externalName"]: event.target.value }) }} inputProps={{ maxLength: 100 }}></TextField>
						</ExternalNameToolTip>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" disabled={isCheckingNameForDupes || isSaving} variant="contained">{isCheckingNameForDupes || isSaving ? "Saving..." : "Save Details"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</section>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</LocalizationProvider>
	)
}

export default ContainerDetailsForm;